// =============================================================================
// MockupUploadButton — apparel side mockup picker + preview
// -----------------------------------------------------------------------------
// Renders the current mockup as a thumbnail when present and exposes a
// file picker (Choose / Replace) and a Clear action. All file validation
// is duplicated from the server (see SidesService.uploadMockup) to give
// the merchant immediate feedback before the network round-trip.
// =============================================================================

import { useRef, useState } from 'react';
import { Button, Spinner } from '@tkpod/ui';
import type { PrintAreaMockupMode } from '@/lib/api/apparel-sides';

/**
 * MASK mode mockups MUST be transparency-preserving so the designer
 * can layer per-variant `backgroundColor` fills behind them. JPEG
 * cannot represent alpha and would defeat the entire MASK pipeline,
 * so we filter it out at the picker. Mirrors the server-side check
 * in `apps/api/src/modules/catalog/sides.service.ts#uploadMockup`.
 */
const TRANSPARENCY_PRESERVING_MIME_TYPES = [
  'image/png',
  'image/webp',
  'image/avif',
] as const;

/**
 * PER_VARIANT mockups are fully-coloured product photos; transparency
 * is irrelevant so JPEG is welcomed too.
 */
const ALL_IMAGE_MIME_TYPES = [
  ...TRANSPARENCY_PRESERVING_MIME_TYPES,
  'image/jpeg',
  'image/jpg',
] as const;

const MAX_BYTES = 10 * 1024 * 1024;

export interface MockupUploadButtonProps {
  mockupImageUrl: string | null;
  isUploading: boolean;
  onUpload: (file: File) => void;
  onClear?: () => void;
  disabled?: boolean;
  /**
   * The side's mockup mode. Drives which mime types are accepted by
   * the picker and the helper text shown beneath it. Defaults to
   * MASK for backward compatibility with older callers.
   */
  mockupMode?: PrintAreaMockupMode;
}

export function MockupUploadButton({
  mockupImageUrl,
  isUploading,
  onUpload,
  onClear,
  disabled,
  mockupMode = 'MASK',
}: MockupUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const allowed =
    mockupMode === 'MASK'
      ? TRANSPARENCY_PRESERVING_MIME_TYPES
      : ALL_IMAGE_MIME_TYPES;
  const allowedLabel =
    mockupMode === 'MASK' ? 'PNG, WebP, or AVIF' : 'PNG, WebP, AVIF, or JPEG';

  const handlePick = () => {
    if (disabled || isUploading) return;
    setValidationError(null);
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!allowed.includes(file.type as never)) {
      setValidationError(
        mockupMode === 'MASK'
          ? `MASK mode requires a transparent format. Allowed: ${allowedLabel}.`
          : `Unsupported file type. Allowed: ${allowedLabel}.`,
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setValidationError(
        `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${MAX_BYTES / 1024 / 1024} MB.`,
      );
      return;
    }
    onUpload(file);
  };

  return (
    <div className="flex items-start gap-4">
      <div
        className="relative flex h-24 w-24 flex-none items-center justify-center overflow-hidden rounded-md border border-border bg-surface-tertiary"
        aria-label="Mockup preview"
      >
        {mockupImageUrl ? (
          // `crossOrigin="anonymous"` aligns admin's mockup preview with the
          // designer's Konva canvas loader (which sets the same flag because
          // it needs `toDataURL`/export). Sharing the CORS policy across
          // every consumer of this URL prevents the browser cache from
          // serving a "non-CORS" entry to a CORS-aware request, which
          // manifests as flaky mockup rendering in the designer.
          <img
            src={mockupImageUrl}
            crossOrigin="anonymous"
            alt="Mockup"
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="px-2 text-center text-[10px] text-text-tertiary">
            No mockup
          </span>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/70">
            <Spinner size="sm" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePick}
            disabled={disabled || isUploading}
          >
            {mockupImageUrl ? 'Replace mockup' : 'Upload mockup'}
          </Button>
          {mockupImageUrl && onClear && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                if (disabled || isUploading) return;
                if (window.confirm('Clear the current mockup image?')) {
                  onClear();
                }
              }}
              disabled={disabled || isUploading}
            >
              Clear
            </Button>
          )}
        </div>
        <p className="mt-1.5 text-xs text-text-tertiary">
          {mockupMode === 'MASK'
            ? `${allowedLabel}. Max 10 MB. Transparent areas reveal the per-variant colour fill behind the mockup.`
            : `${allowedLabel}. Max 10 MB. This mockup is the side-level fallback; per-variant photos override it in the variant matrix.`}
        </p>
        {validationError && (
          <p className="mt-1 text-xs text-danger-600">{validationError}</p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={allowed.join(',')}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}
